/**
 * ContactTable - Tabela de contatos usando DataTable genérico
 * Mantém funcionalidade específica: telefone/whatsapp, email
 * Princípios: DRY (reutiliza DataTable + ChatAvatar), SRP (apenas configuração de colunas)
 */
import React, { useMemo } from 'react';
import { Contact } from '../../../types';
import DataTable, { Column } from '../../../components/shared/DataTable';
import ActionMenu, { createEditAction, createDeleteAction, createDuplicateAction, createExportAction } from '../../../components/shared/ActionMenu';
import { AppIcon } from '../../../components/icons/AppIcon';
import { isUsableContactName } from '../../../utils/phoneUtils';
import { formatPhoneWithDDI } from '../../../utils/formatters';
import { ChatAvatar } from '../../chat/components/chat-window/ChatAvatar';

interface ContactTableProps {
    contacts: Contact[];
    isLoading?: boolean;
    onEdit: (contact: Contact) => void;
    onDelete: (contact: Contact) => void;
    onDuplicate: (contact: Contact) => void;
    onChat?: (contact: Contact) => void;
    onExport?: (contact: Contact) => void;
    showCheckbox?: boolean;
    selectedIds?: Set<string>;
    allSelected?: boolean;
    someSelected?: boolean;
    onToggleSelection?: (id: string) => void;
    onToggleAll?: () => void;
    footer?: React.ReactNode;
}

function contactPhone(contact: Contact): string {
    return contact.whatsapp || contact.phone || '';
}

function ContactIdentityCell({
    contact,
    onEdit,
}: {
    contact: Contact;
    onEdit: (contact: Contact) => void;
}) {
    const phone = contactPhone(contact);
    const named = isUsableContactName(contact.name, phone);
    const ariaName = named ? contact.name : phone ? formatPhoneWithDDI(phone) : 'Sem nome';

    return (
        <div className="flex min-w-0 items-center gap-s3">
            <div className="shrink-0" aria-hidden>
                <ChatAvatar
                    name={ariaName}
                    size="lg"
                />
            </div>
            <div className="min-w-0">
                {named ? (
                    <p className="truncate text-base font-medium text-default">{contact.name}</p>
                ) : (
                    <>
                        <p className="truncate text-base italic text-muted">Sem nome</p>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit(contact);
                            }}
                            className="mt-s1 text-xs font-medium text-primary transition-colors hover:text-primary-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                            aria-label={`Adicionar nome para ${ariaName}`}
                        >
                            + Adicionar nome
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

const ContactTable: React.FC<ContactTableProps> = ({
    contacts, isLoading, onEdit, onDelete, onDuplicate, onChat, onExport,
    showCheckbox = false, selectedIds = new Set(), onToggleSelection, onToggleAll,
    allSelected, someSelected, footer
}) => {
    const columns: Column<Contact>[] = useMemo(() => [
        {
            key: 'name', header: 'Contato',
            render: (contact) => <ContactIdentityCell contact={contact} onEdit={onEdit} />,
        },
        {
            key: 'company', header: 'Empresa',
            render: (contact) =>
                contact.company ? (
                    <span className="text-secondary">{contact.company}</span>
                ) : (
                    <span className="italic text-muted">Sem empresa</span>
                ),
        },
        {
            key: 'phone', header: 'Telefone',
            render: (contact) => {
                const phone = contactPhone(contact);
                return phone ? (
                    <span className="font-mono text-sm tabular-nums text-secondary">
                        {formatPhoneWithDDI(phone)}
                    </span>
                ) : (
                    <span className="italic text-muted">Sem telefone</span>
                );
            },
        },
        {
            key: 'email', header: 'Email',
            render: (contact) =>
                contact.email ? (
                    <span className="text-secondary">{contact.email}</span>
                ) : (
                    <span className="italic text-muted">Sem e-mail</span>
                ),
        },
        {
            key: 'actions', header: 'Ações', align: 'right',
            render: (contact) => {
                const phone = contactPhone(contact);
                const named = isUsableContactName(contact.name, phone);
                const label = named
                    ? contact.name
                    : phone
                      ? formatPhoneWithDDI(phone)
                      : 'Sem nome';

                return (
                    <div className="flex items-center justify-end gap-s1">
                        <button
                            type="button"
                            onClick={() => onChat?.(contact)}
                            className="flex h-10 w-10 items-center justify-center rounded-lg text-default transition-colors hover:bg-primary-subtle hover:text-primary touch-manipulation md:h-auto md:w-auto md:rounded-full md:p-1.5 md:text-secondary"
                            title="Iniciar conversa"
                            aria-label={`Conversar com ${label}`}
                        >
                            <AppIcon name="message" className="size-5" aria-hidden />
                        </button>
                        <ActionMenu
                            actions={[
                                ...(onExport ? [createExportAction(() => onExport(contact))] : []),
                                createEditAction(() => onEdit(contact)),
                                createDuplicateAction(() => onDuplicate(contact)),
                                createDeleteAction(() => onDelete(contact)),
                            ]}
                            buttonClassName="size-11 rounded-md border-subtle md:size-9 md:p-0"
                        />
                    </div>
                );
            },
        },
    ], [onEdit, onDuplicate, onDelete, onChat, onExport]);

    return (
        <DataTable
            columns={columns}
            data={contacts}
            getRowKey={(c) => c.id}
            emptyMessage="Nenhum contato encontrado."
            isLoading={isLoading}
            selectable={showCheckbox}
            selectedIds={selectedIds}
            onToggleSelection={onToggleSelection}
            onToggleAll={onToggleAll}
            allSelected={allSelected}
            someSelected={someSelected}
            footer={footer}
        />
    );
};

export default ContactTable;
